import './settings/settings-leaf.css';
import ProviderUsage from './provider-usage.tsx';
import TwilioSetupStatus from './settings/twilio-setup-status.tsx';
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { companyApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { useAppConfig } from './turnstile.tsx';
import { HelpDocLink, NUMBER_CUTOVER_DOC_SLUG } from './help-doc-link.tsx';
import { Alert, Button, Checkbox, LoadingBlock, TextInput } from './ui.tsx';
import { PLANS, minimumPlanFor, planHasFeature } from '@shared/constants/index.ts';
import type { SmsSettings, TwilioTakeoverOption } from '@shared/types/index.ts';

/**
 * A company's own Twilio account, for Settings.
 *
 * Every company texts from a number of its own: normally the one the
 * installation issues it (the dedicated-number panel), or — for a company that
 * already has Twilio — its own account, connected here. When connected and
 * switched on, the company's account is what sends; unticking the switch goes
 * back to the issued number without deleting anything.
 *
 * The Auth Token and API key secret are write-only, like the payment keys next
 * door: what comes back identifies each by its last four and never repeats it.
 * The server checks the details with Twilio before saving them, then sets up
 * the number's webhooks itself (`TwilioSetupStatus` shows how that went), so
 * there is no URL to paste into Twilio.
 *
 * Where the deployment offers Twilio Connect (`/config` →
 * `twilioConnectAvailable`), connecting is one button instead: the owner
 * approves BlastoClean on Twilio's own page and comes back with a
 * `?twilioConnect=` flag, then picks the area code of the number BlastoClean
 * buys in the connected account. The credentials form stays behind "Use API
 * credentials instead".
 */
export default function SmsSettingsSection() {
  const { t } = useTranslation();
  const { can, refresh, user, company } = useSession();
  const appConfig = useAppConfig();
  const connectAvailable = appConfig.data?.twilioConnectAvailable ?? false;

  // Outbound texting of any kind is a Pro-tier capability, so below that tier
  // the credential forms give way to an upsell — the Worker would refuse the
  // sends regardless of what was stored here.
  const planGated = company !== null && !planHasFeature(company.effectivePlan, 'sms');

  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  // Null means "not edited": the stored key SID is shown, and an empty string
  // is how an owner removes it.
  const [apiKeySid, setApiKeySid] = useState<string | null>(null);
  const [apiKeySecret, setApiKeySecret] = useState('');
  // Null means "not edited yet, show what the server has": the settings query
  // resolves after the first render, so seeding state from it would freeze the
  // fields at their pre-load defaults.
  const [messagingServiceSid, setMessagingServiceSid] = useState<string | null>(null);
  const [fromNumber, setFromNumber] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  /**
   * Where a test message goes. Starts from the sender's own number so the
   * common case is one click, and stays editable so it is never *only* their
   * own number — someone setting this up for an office often has the office
   * handset in front of them instead.
   */
  const [testPhone, setTestPhone] = useState(() => user?.phone ?? '');
  /** The area code of the number to buy in an account connected through Twilio Connect. */
  const [areaCode, setAreaCode] = useState('');
  /** Where Connect is offered, whether the owner opened the pasted-credentials form instead. */
  const [showCredentials, setShowCredentials] = useState(false);

  const canEdit = can('company:write');
  const queryClient = useQueryClient();
  /** The texting registration card reads the same account: a new account or number is a new registration. */
  const refreshRegistration = () => void queryClient.invalidateQueries({ queryKey: ['own-a2p'] });
  const settings = useQuery({
    queryKey: ['sms-settings'],
    queryFn: () => companyApi.smsSettings(),
    enabled: canEdit && !planGated,
  });
  // The registration card's own query. A campaign on the Messaging Service
  // BlastoClean set up stays, and is billed monthly, after pasted credentials
  // are disconnected: the note beside Disconnect says so.
  const pastedAccount = settings.data?.configured === true && settings.data.connection !== 'connect';
  const registration = useQuery({
    queryKey: ['own-a2p'],
    queryFn: () => companyApi.ownA2p(),
    enabled: canEdit && !planGated && pastedAccount,
  });
  // One filed in the Console and not checked for yet counts too.
  const untracked = registration.data?.untrackedCampaign;
  const registrationFiled =
    pastedAccount && (Boolean(registration.data?.registration?.campaignSid) || untracked === true);
  // Disconnect waits for that answer, a refetch's too (an answer kept from
  // before can be out of date); one that could not be had says it may apply.
  const registrationPending = pastedAccount && (registration.isPending || registration.isFetching);
  const registrationUnknown = pastedAccount && (registration.isError || (!registrationFiled && untracked === null));
  // Another Account SID replaces the account, and leaves that registration
  // behind just as Disconnect does: the note under the field says so first.
  const typedSid = accountSid.trim().toLowerCase();
  const replacingAccount =
    pastedAccount && typedSid !== '' && typedSid !== (settings.data?.accountSid ?? '').toLowerCase();
  // Saving it waits for that answer, as Disconnect does, so the note is seen first.
  const replacementPending = replacingAccount && registrationPending;

  function afterSetUp(result: SmsSettings, confirmed: boolean) {
    setFieldErrors({});
    void settings.refetch();
    refreshRegistration();
    // Agreeing to a takeover that still needs agreeing means Twilio changed
    // since the card was drawn: say so, rather than seeming to do nothing.
    if (confirmed && result.twilioSetup?.status === 'needs_confirmation') {
      setMessage({ tone: 'error', text: t('smsSettings.setup.changedSince') });
      return;
    }
    setMessage(
      result.twilioSetup?.status === 'configured'
        ? { tone: 'success', text: t('smsSettings.setup.doneMessage') }
        : null,
    );
  }

  function afterChange(text: string) {
    setAccountSid('');
    setAuthToken('');
    setApiKeySid(null);
    setApiKeySecret('');
    setMessagingServiceSid(null);
    setFromNumber(null);
    setFieldErrors({});
    setMessage({ tone: 'success', text });
    void settings.refetch();
    refreshRegistration();
    // `smsConfigured` has just changed, and connecting an account is what makes
    // the customer form offer the text-reminder consent box at all — so the
    // flag has to be re-read, not just this panel.
    //
    // It rides on the session's company rather than a query of its own, so the
    // ['company'] key this used to invalidate matched nothing: no query is
    // registered under it anywhere, and the consent box went on hiding until
    // the next full page load.
    void refresh();
  }

  function onFailure(caught: unknown, fallback: string) {
    if (caught instanceof ApiRequestError) {
      setFieldErrors(caught.fields);
      setMessage(Object.keys(caught.fields).length > 0 ? null : { tone: 'error', text: caught.message });
    } else {
      setMessage({ tone: 'error', text: fallback });
    }
  }

  const save = useMutation({
    mutationFn: (body: {
      accountSid?: string;
      authToken?: string;
      apiKeySid?: string;
      apiKeySecret?: string;
      messagingServiceSid?: string;
      fromNumber?: string;
      enabled?: boolean;
    }) => companyApi.saveSmsSettings(body),
    onSuccess: () => afterChange(t('smsSettings.savedMessage')),
    onError: (caught) => onFailure(caught, t('smsSettings.saveError')),
  });

  const sendTest = useMutation({
    mutationFn: () => companyApi.sendTestSms(testPhone.trim()),
    onSuccess: (result) => {
      setFieldErrors({});
      setMessage(
        result.delivered
          ? { tone: 'success', text: t('smsSettings.testSent', { recipient: result.sentTo }) }
          : {
              tone: 'error',
              // The provider's own words, verbatim. Someone debugging their
              // credentials needs Twilio's answer, not a paraphrase of it.
              text: t('smsSettings.testFailed', {
                recipient: result.sentTo,
                reason: result.failureReason ?? t('smsSettings.testFailedNoReason'),
              }),
            },
      );
    },
    onError: (caught) => onFailure(caught, t('smsSettings.testError')),
  });

  const setUp = useMutation({
    mutationFn: (confirm?: { option: TwilioTakeoverOption; target: string }) => companyApi.twilioSetup(confirm),
    onSuccess: (result, confirm) => afterSetUp(result, Boolean(confirm)),
    onError: (caught) => onFailure(caught, t('smsSettings.setup.error')),
  });

  const disconnect = useMutation({
    mutationFn: () => companyApi.disconnectSms(),
    onSuccess: () => afterChange(t('smsSettings.disconnectedMessage')),
    onError: (caught) => onFailure(caught, t('smsSettings.disconnectError')),
  });

  // "Connect your Twilio account": the API answers Twilio's approval page and
  // the whole page leaves for it. The callback lands back on Settings with a
  // ?twilioConnect= flag the effect below words for a person.
  const connectStart = useMutation({
    mutationFn: () => companyApi.twilioConnectStart(),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (caught) => onFailure(caught, t('smsSettings.connect.startError')),
  });

  // A purchase that failed after Twilio may have bought the number (the
  // server marks it): until it is saved, Disconnect says a number stays billed.
  const [unsavedPurchase, setUnsavedPurchase] = useState(false);
  const buyNumber = useMutation({
    mutationFn: () => companyApi.buyTwilioConnectNumber(areaCode.trim()),
    onSuccess: (result) => {
      setAreaCode('');
      afterChange(t('smsSettings.connect.numberBought', { number: result.fromNumber ?? '' }));
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError && caught.fields.purchase) setUnsavedPurchase(true);
      onFailure(caught, t('smsSettings.connect.buyError'));
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('twilioConnect');
    if (!outcome) return;
    params.delete('twilioConnect');
    const query = params.toString();
    history.replaceState(
      history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
    const refusals: Record<string, string> = {
      declined: t('smsSettings.connect.result.declined'),
      expired: t('smsSettings.connect.result.expired'),
      'in-use': t('smsSettings.connect.result.inUse'),
      'not-authorized': t('smsSettings.connect.result.notAuthorized'),
      refused: t('smsSettings.connect.result.refused'),
      unavailable: t('smsSettings.connect.result.unavailable'),
    };
    if (outcome === 'connected') {
      setMessage({ tone: 'success', text: t('smsSettings.connect.result.connected') });
      // An account connected again has its number back (the Worker found it in
      // the subaccount): said so, rather than asking for one below.
      void settings.refetch().then(({ data }) => {
        if (data?.connection === 'connect' && data.fromNumber)
          setMessage({
            tone: 'success',
            text: t('smsSettings.connect.result.connectedWithNumber', { number: data.fromNumber }),
          });
      });
      void refresh();
    } else {
      setMessage({ tone: 'error', text: refusals[outcome] ?? t('smsSettings.connect.result.error') });
    }
    // Once, on mount: the flag is consumed off the URL it arrived on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (replacementPending) return;
    setMessage(null);
    save.mutate({
      ...(accountSid.trim() ? { accountSid: accountSid.trim() } : {}),
      ...(authToken.trim() ? { authToken: authToken.trim() } : {}),
      ...(apiKeySid === null ? {} : { apiKeySid: apiKeySid.trim() }),
      ...(apiKeySecret.trim() ? { apiKeySecret: apiKeySecret.trim() } : {}),
      // Sent as typed rather than only when non-empty: an empty field is how
      // one of the two senders is cleared, and skipping it would make a
      // Messaging Service impossible to remove.
      ...(messagingServiceSid === null ? {} : { messagingServiceSid: messagingServiceSid.trim() }),
      ...(fromNumber === null ? {} : { fromNumber: fromNumber.trim() }),
    });
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setMessage({ tone: 'error', text: t('smsSettings.copyError') });
    }
  }

  if (!canEdit) return null;

  const data = settings.data;
  // FEATURE_MANAGED_SMS off and no issued number that can send: connecting an
  // own Twilio account is the only way to text, so the card says that. With a
  // usable issued number, the own account stays optional.
  const ownOnly = company?.managedSmsAvailable === false && !data?.managedNumber?.usable;
  // The one-button connection, for a company with no account stored yet.
  const offerConnect = connectAvailable && data !== undefined && !data.configured;

  if (planGated) {
    return (
      <section className="card work-section settings-leaf mb-6" aria-labelledby="sms-credentials-heading">
        <div className="work-section-heading">
          <h2 id="sms-credentials-heading" className="mb-3 text-lg font-semibold">
            {ownOnly ? t('smsSettings.ownOnly.heading') : t('smsSettings.heading')}
          </h2>
        </div>
        <Alert tone="info" title={t('smsSettings.upgradeTitle')}>
          <p>{t('smsSettings.upgradeNote', { plan: PLANS[minimumPlanFor('sms')].name })}</p>
          {/* A path, not a bare hash: this card lives on /settings/texting now. */}
          <Link to="/settings#settings-billing" className="mt-2 inline-block font-semibold hover:underline">
            {t('smsSettings.upgradeCta')}
          </Link>
        </Alert>
      </section>
    );
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="sms-credentials-heading">
      <div className="work-section-heading">
        <h2 id="sms-credentials-heading" className="mb-3 text-lg font-semibold">
          {ownOnly ? t('smsSettings.ownOnly.heading') : t('smsSettings.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {ownOnly ? t('smsSettings.ownOnly.description') : t('smsSettings.description')}
        <HelpDocLink slug={NUMBER_CUTOVER_DOC_SLUG}>{t('smsSettings.cutoverGuide')}</HelpDocLink>
      </p>
      <ProviderUsage kind="sms" />

      {settings.isLoading ? <LoadingBlock label={t('smsSettings.loading')} /> : null}

      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {data ? (
        <>
          {/*
            Which number is actually sending, stated before the fields, with
            the way back. Connecting an account and leaving it switched on is
            the owner's choice to send (and be billed) through it.
          */}
          {data.activeSource === 'company' ? (
            <div className="mb-4">
              <Alert tone="info">{t('smsSettings.companyTakesPrecedence')}</Alert>
            </div>
          ) : data.activeSource === 'platform_managed' && data.configured ? (
            <div className="mb-4">
              <Alert tone="info">{t('smsSettings.managedTakesPrecedence')}</Alert>
            </div>
          ) : null}

          {/*
            Twilio signs webhooks with the Auth Token only. Saving requires one,
            so this shows only for a row whose token has gone missing.
          */}
          {data.configured && !data.webhooksVerifiable ? (
            <div className="mb-4">
              <Alert tone="warning" title={t('smsSettings.webhooksUnverifiableTitle')}>
                {t('smsSettings.webhooksUnverifiableBody')}
              </Alert>
            </div>
          ) : null}

          {/*
            The one state that used to be invisible: a complete-looking row
            whose auth token cannot be decrypted, which is what a rotated or
            missing PAYMENT_ENCRYPTION_KEY leaves behind. The panel said
            "connected" and the reminders card at the top of the page said "not
            set up", and both were right about different questions. Saying it
            plainly, with the one action that fixes it, beats either. A connected
            account with no number yet is not that: it waits for the number
            picker below.
          */}
          {data.configured && data.enabled && !data.usable && !(data.connection === 'connect' && !data.fromNumber) ? (
            <div className="mb-4">
              <Alert tone="warning" title={t('smsSettings.unusableTitle')}>
                {t('smsSettings.unusableBody')}
              </Alert>
            </div>
          ) : null}

          <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-semibold">{t('smsSettings.statusLabel')}</dt>
              <dd className="text-[color:var(--color-muted)]">
                {data.activeSource === 'platform_managed'
                  ? // Sending on the number the installation issued this
                    // company. Without its own branch this fell through to
                    // "No Twilio account connected" — true of the fields on
                    // this panel, and badly wrong as a summary of whether
                    // their texts are going out.
                    t('smsSettings.statusManaged')
                  : data.activeSource === 'company'
                    ? t('smsSettings.statusCompany')
                    : data.connection === 'connect' && !data.fromNumber
                      ? t('smsSettings.connect.statusNeedsNumber')
                      : // Saved but unusable is its own answer. Folding it into
                        // "switched off" would send an owner to the checkbox,
                        // which is not the problem and would not fix it.
                        data.configured && data.enabled && !data.usable
                        ? t('smsSettings.statusUnusable')
                        : data.configured
                          ? t('smsSettings.statusConnectedOff')
                          : t('smsSettings.statusNotConnected')}
              </dd>
            </div>
            <div>
              <dt className="font-semibold">{t('smsSettings.accountSidLabel')}</dt>
              <dd className="break-all text-[color:var(--color-muted)]">
                {data.accountSid ?? t('smsSettings.notSet')}
              </dd>
            </div>
            {data.connection === 'connect' ? (
              // Nothing of the owner's is stored: BlastoClean acts through its Connect App.
              <div>
                <dt className="font-semibold">{t('smsSettings.connect.connectionLabel')}</dt>
                <dd className="text-[color:var(--color-muted)]">{t('smsSettings.connect.connectedVia')}</dd>
              </div>
            ) : (
              <div>
                <dt className="font-semibold">{t('smsSettings.authTokenLabel')}</dt>
                <dd className="text-[color:var(--color-muted)]">
                  {data.authTokenLast4 ? `•••• ${data.authTokenLast4}` : t('smsSettings.notSet')}
                </dd>
              </div>
            )}
            {data.apiKeySid ? (
              <div>
                <dt className="font-semibold">{t('smsSettings.apiKeyLabel')}</dt>
                <dd className="break-all text-[color:var(--color-muted)]">
                  {data.apiKeySid}
                  {data.apiKeySecretLast4 ? ` · •••• ${data.apiKeySecretLast4}` : ''}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="font-semibold">{t('smsSettings.senderLabel')}</dt>
              <dd className="break-all text-[color:var(--color-muted)]">
                {data.messagingServiceSid ?? data.fromNumber ?? t('smsSettings.notSet')}
              </dd>
            </div>
          </dl>

          {data.configured && (data.connection !== 'connect' || data.fromNumber) ? (
            <TwilioSetupStatus
              settings={data}
              busy={setUp.isPending}
              onSetUp={(confirm) => {
                setMessage(null);
                setUp.mutate(confirm);
              }}
              onCopy={(url) => void copyUrl(url)}
              copied={copied}
            />
          ) : null}

          {/*
            Connected through Twilio Connect with no number yet: the account
            Twilio made for BlastoClean starts empty, so the owner picks the
            area code of the number BlastoClean buys in it.
          */}
          {data.connection === 'connect' && !data.fromNumber ? (
            <div className="mb-4 border-t border-[color:var(--color-line)] pt-4" data-testid="twilio-connect-number">
              <p className="mb-1 text-sm font-semibold">{t('smsSettings.connect.numberHeading')}</p>
              <p className="mb-2 text-sm text-[color:var(--color-muted)]">
                {t('smsSettings.connect.numberDescription')}
              </p>
              <div className="grid max-w-md gap-1">
                <TextInput
                  label={t('smsSettings.connect.areaCodeLabel')}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={3}
                  hint={t('smsSettings.connect.areaCodeHint')}
                  value={areaCode}
                  error={fieldErrors.areaCode}
                  onChange={(event) => setAreaCode(event.target.value)}
                />
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    busy={buyNumber.isPending}
                    disabled={!/^\d{3}$/.test(areaCode.trim())}
                    onClick={() => {
                      setMessage(null);
                      buyNumber.mutate();
                    }}
                  >
                    {t('smsSettings.connect.buyNumber')}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {/*
            Offered only once there is something to test. A send button on a
            page with no credentials behind it can only ever produce the error
            the status line above already states.
          */}
          {data.activeSource ? (
            <div className="mb-4 border-t border-[color:var(--color-line)] pt-4">
              <p className="mb-1 text-sm font-semibold">{t('smsSettings.testHeading')}</p>
              <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('smsSettings.testDescription')}</p>
              <div className="grid max-w-md gap-1">
                <TextInput
                  label={t('smsSettings.testPhoneLabel')}
                  type="tel"
                  autoComplete="tel"
                  hint={t('smsSettings.testPhoneHint')}
                  value={testPhone}
                  error={fieldErrors.recipientPhone}
                  onChange={(event) => setTestPhone(event.target.value)}
                />
                <div>
                  <Button
                    type="button"
                    busy={sendTest.isPending}
                    disabled={!testPhone.trim()}
                    onClick={() => {
                      setMessage(null);
                      sendTest.mutate();
                    }}
                  >
                    {t('smsSettings.sendTest')}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {/*
            One button, where the deployment offers it: Twilio's own page, and
            back, with nothing to paste.
          */}
          {offerConnect ? (
            <div className="mb-4" data-testid="twilio-connect">
              <p className="mb-1 text-sm font-semibold">{t('smsSettings.connect.heading')}</p>
              <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('smsSettings.connect.description')}</p>
              <Button
                type="button"
                variant="primary"
                busy={connectStart.isPending}
                onClick={() => {
                  setMessage(null);
                  connectStart.mutate();
                }}
              >
                {t('smsSettings.connect.button')}
              </Button>
            </div>
          ) : null}

          {data.connection === 'connect' ? (
            <ConnectedAccountControls
              enabled={data.enabled}
              boughtNumber={Boolean(data.fromNumber) || unsavedPurchase}
              ownOnly={ownOnly}
              saving={save.isPending}
              disconnecting={disconnect.isPending}
              buying={buyNumber.isPending}
              onToggle={(enabled) => {
                setMessage(null);
                save.mutate({ enabled });
              }}
              onDisconnect={() => disconnect.mutate()}
            />
          ) : null}

          {/*
            Where Connect is offered, pasting credentials waits behind this:
            most owners never need it. An account connected through Connect
            has nothing to paste or edit, so its form is not shown at all.
          */}
          {offerConnect ? (
            <button
              type="button"
              className="mb-2 text-sm font-semibold hover:underline"
              aria-expanded={showCredentials}
              aria-controls="sms-credentials-form"
              onClick={() => setShowCredentials((shown) => !shown)}
            >
              {t('smsSettings.connect.useCredentials')}
            </button>
          ) : null}

          <form
            id="sms-credentials-form"
            onSubmit={handleSubmit}
            noValidate
            hidden={data.connection === 'connect' || (offerConnect && !showCredentials)}
          >
            <TextInput
              label={t('smsSettings.accountSidFieldLabel')}
              autoComplete="off"
              placeholder={data.accountSid ?? 'AC…'}
              hint={t('smsSettings.accountSidHint')}
              value={accountSid}
              error={fieldErrors.accountSid}
              onChange={(event) => setAccountSid(event.target.value)}
            />
            {replacingAccount && (registrationFiled || registrationUnknown) ? (
              <p
                className="-mt-2 mb-3 text-xs text-[color:var(--color-muted)]"
                data-testid="replace-account-registration-note"
              >
                {t(
                  registrationFiled
                    ? 'smsSettings.replaceAccountRegistrationNote'
                    : 'smsSettings.replaceAccountRegistrationMaybeNote',
                  { accountSid: data.accountSid ?? '' },
                )}
              </p>
            ) : null}
            <TextInput
              label={t('smsSettings.authTokenFieldLabel')}
              type="password"
              autoComplete="off"
              placeholder={
                data.authTokenLast4
                  ? t('smsSettings.authTokenPlaceholderKeep')
                  : t('smsSettings.authTokenPlaceholderNew')
              }
              hint={t('smsSettings.authTokenHint')}
              value={authToken}
              error={fieldErrors.authToken}
              onChange={(event) => setAuthToken(event.target.value)}
            />
            <div className="grid gap-x-4 sm:grid-cols-2">
              <TextInput
                label={t('smsSettings.apiKeySidFieldLabel')}
                autoComplete="off"
                placeholder="SK…"
                hint={t('smsSettings.apiKeySidHint')}
                value={apiKeySid ?? data.apiKeySid ?? ''}
                error={fieldErrors.apiKeySid}
                onChange={(event) => setApiKeySid(event.target.value)}
              />
              <TextInput
                label={t('smsSettings.apiKeySecretFieldLabel')}
                type="password"
                autoComplete="off"
                placeholder={
                  data.apiKeySecretLast4
                    ? t('smsSettings.apiKeySecretPlaceholderKeep')
                    : t('smsSettings.apiKeySecretPlaceholderNew')
                }
                hint={t('smsSettings.apiKeySecretHint')}
                value={apiKeySecret}
                error={fieldErrors.apiKeySecret}
                onChange={(event) => setApiKeySecret(event.target.value)}
              />
            </div>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <TextInput
                label={t('smsSettings.messagingServiceFieldLabel')}
                autoComplete="off"
                placeholder="MG…"
                hint={t('smsSettings.messagingServiceHint')}
                value={messagingServiceSid ?? data.messagingServiceSid ?? ''}
                error={fieldErrors.messagingServiceSid}
                onChange={(event) => setMessagingServiceSid(event.target.value)}
              />
              <TextInput
                label={t('smsSettings.fromNumberFieldLabel')}
                autoComplete="off"
                placeholder="+15551234567"
                hint={t('smsSettings.fromNumberHint')}
                value={fromNumber ?? data.fromNumber ?? ''}
                error={fieldErrors.fromNumber}
                onChange={(event) => setFromNumber(event.target.value)}
              />
            </div>

            <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('smsSettings.verifyNote')}</p>

            {data.configured ? (
              <Checkbox
                label={t('smsSettings.enabledLabel')}
                hint={ownOnly ? t('smsSettings.ownOnly.enabledHint') : t('smsSettings.enabledHint')}
                checked={data.enabled}
                onChange={(event) => {
                  setMessage(null);
                  save.mutate({ enabled: event.target.checked });
                }}
              />
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="submit" variant="primary" busy={save.isPending} disabled={replacementPending}>
                {data.configured ? t('smsSettings.saveSmsSettings') : t('smsSettings.connectTwilio')}
              </Button>
              {data.configured ? (
                <Button
                  type="button"
                  variant="secondary"
                  busy={disconnect.isPending}
                  disabled={registrationPending}
                  onClick={() => disconnect.mutate()}
                >
                  {t('smsSettings.disconnect')}
                </Button>
              ) : null}
            </div>
            {registrationFiled || registrationUnknown ? (
              <p className="mt-2 text-xs text-[color:var(--color-muted)]" data-testid="disconnect-registration-note">
                {t(
                  registrationFiled
                    ? 'smsSettings.disconnectRegistrationNote'
                    : 'smsSettings.disconnectRegistrationMaybeNote',
                )}
              </p>
            ) : null}
          </form>
        </>
      ) : null}
    </section>
  );
}

/**
 * An account connected through Twilio Connect: the switch and the way out.
 * There is nothing to edit, so no form; the note says what disconnecting
 * leaves in Twilio, because the Connect App cannot release the number it
 * bought there or remove itself.
 */
function ConnectedAccountControls({
  enabled,
  boughtNumber,
  ownOnly,
  saving,
  disconnecting,
  buying,
  onToggle,
  onDisconnect,
}: {
  enabled: boolean;
  /** BlastoClean bought a number in the account: only then does one stay billed after a disconnect. */
  boughtNumber: boolean;
  ownOnly: boolean;
  saving: boolean;
  disconnecting: boolean;
  /**
   * A number is being bought: switching off or disconnecting now would only
   * queue behind a purchase that cannot be undone, and leave the number
   * billed in Twilio with nothing using it.
   */
  buying: boolean;
  onToggle: (enabled: boolean) => void;
  onDisconnect: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="twilio-connected">
      <Checkbox
        label={t('smsSettings.enabledLabel')}
        hint={ownOnly ? t('smsSettings.ownOnly.enabledHint') : t('smsSettings.enabledHint')}
        checked={enabled}
        disabled={saving || buying}
        onChange={(event) => onToggle(event.target.checked)}
      />
      <p className="mb-3 mt-3 text-sm text-[color:var(--color-muted)]">
        {t(boughtNumber ? 'smsSettings.connect.disconnectNote' : 'smsSettings.connect.disconnectNoteNoNumber')}
      </p>
      <Button type="button" variant="secondary" busy={disconnecting} disabled={buying} onClick={onDisconnect}>
        {t('smsSettings.disconnect')}
      </Button>
    </div>
  );
}
